import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { default as Client } from 'models/Client';
import GroupType from 'models/GroupType';
import { default as Profile } from 'models/Profile';
import { forResourceClients, useInfiniteClientOptions } from 'services/api/client';
import { updateProfile } from 'services/api/profile';
import { useInboundReferralsInsightsActions } from 'stores/inboundReferralsInsightsStore';
import { useInsightsActions } from 'stores/insightsStore';
import { useAllPatientsActions, useCoordinatePatientsActions } from 'stores/patientFiltersStore';
import { usePortfolioActions } from 'stores/portfolioStore';

const FormContainer = styled.div`
  padding-bottom: 20px;
`;

export type SelectViewModalType = {
  setShow: (b: boolean) => void;
  profile: Profile;
};

export function SelectViewModal(props: SelectViewModalType) {
  const queryClient = useQueryClient();
  const { setShow, profile } = props;
  const [currentClient, setCurrentClient] = useState<Client | null>(profile?.actingClient);
  const navigate = useNavigate();
  const location = useLocation();
  const { reset: resetPortfolioStore } = usePortfolioActions();
  const { reset: resetConnectPatientsStore } = useCoordinatePatientsActions();
  const { reset: resetAllPatientsStore } = useAllPatientsActions();
  const { setSelectedGroupType } = useInsightsActions();
  const { resetFilters: resetInboundReferralsInsightsFilters, closeSlideout: closeInboundReferralsInsightsSlideout } =
    useInboundReferralsInsightsActions();

  const asyncClientOptions = useInfiniteClientOptions({
    leaf: true,
    sortBy: 'name',
  });

  const { mutate, isPending } = useMutation({
    mutationFn: updateProfile,
  });

  useEffect(() => {
    if (currentClient == null) setCurrentClient(profile?.actingClient);
  }, [currentClient, profile?.actingClient]);

  const cancel = () => {
    setShow(false);
  };

  // Resolve the resource on the current page (patient story or referral), if any.
  const resourceForPath = () => {
    const referralId = location.pathname.match(/^\/referrals\/([^/]+)/)?.[1];
    if (referralId) return { params: { id: referralId, type: 'referral' }, fallback: '/referrals' };

    const episodeId = location.pathname.match(/^\/patients\/([^/]+)/)?.[1];
    if (episodeId) return { params: { id: episodeId, type: 'episode' }, fallback: '/patients' };

    const workflowId = location.pathname.match(/^\/inbound-referrals\/([^/]+)/)?.[1];
    if (workflowId) return { params: { id: workflowId, type: 'workflow' }, fallback: '/inbound-referrals' };

    return null;
  };

  // Stay put if the newly-selected view can also access the resource on the current page.
  const canStayOnResource = async (resource: ReturnType<typeof resourceForPath>, newClientId?: string) => {
    if (!resource || !newClientId) return false;

    try {
      const { data } = await forResourceClients(resource.params, new AbortController().signal);
      return data.some((client) => client.id === newClientId);
    } catch {
      return false;
    }
  };

  const submit = async () => {
    const newClientId = currentClient?.id;
    const resource = resourceForPath();
    const stayOnResource = await canStayOnResource(resource, newClientId);

    mutate(
      { actingClientId: newClientId },
      {
        onSuccess: (updatedProfile) => {
          resetPortfolioStore();
          resetConnectPatientsStore();
          resetAllPatientsStore();
          resetInboundReferralsInsightsFilters();
          closeInboundReferralsInsightsSlideout();
          setSelectedGroupType({} as GroupType);
          queryClient.clear();
          if (stayOnResource) {
            // Re-seed the profile so the resource subtree stays mounted (a brief undefined profile
            // would remount it and re-run the auth check, reverting the chosen view).
            queryClient.setQueryData(['profile'], updatedProfile);
          } else {
            navigate(resource?.fallback ?? '/patients');
          }
          setShow(false);
        },
      }
    );
  };

  const changed = useMemo(() => {
    return currentClient?.id !== profile?.actingClient?.id;
  }, [currentClient, profile?.actingClient]);

  return (
    <StandardModal title='Select View' onCancel={cancel}>
      <FormContainer>
        <InputGroup title='Select a view'>
          <Select<Client>
            {...asyncClientOptions}
            placeholder='Select a view'
            getOptionValue={(client) => client.id}
            getOptionLabel={(client) => client.name}
            value={currentClient}
            onChange={(client) => {
              setCurrentClient(client);
            }}
          />
        </InputGroup>
      </FormContainer>
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!changed} loading={isPending}>
          Confirm
        </Button>
      </Actions>
    </StandardModal>
  );
}
